import React from "react";

/**
 * Cherry Blossom PNG Assets from /design-elements/
 */
export const CHERRY_ASSETS = [
  "https://assets.auxodev.dk/cdn-cgi/image/width=600,format=auto,quality=85/assets/71339d65-a05d-4c02-a540-2260acdfc245/cherry-1.png",
  "https://assets.auxodev.dk/cdn-cgi/image/width=600,format=auto,quality=85/assets/71339d65-a05d-4c02-a540-2260acdfc245/cherry-2.png",
  "https://assets.auxodev.dk/cdn-cgi/image/width=600,format=auto,quality=85/assets/71339d65-a05d-4c02-a540-2260acdfc245/cherry-3.png",
  "https://assets.auxodev.dk/cdn-cgi/image/width=600,format=auto,quality=85/assets/71339d65-a05d-4c02-a540-2260acdfc245/cherry-4.png",
  "https://assets.auxodev.dk/cdn-cgi/image/width=600,format=auto,quality=85/assets/71339d65-a05d-4c02-a540-2260acdfc245/cherry-5.png",
  "https://assets.auxodev.dk/cdn-cgi/image/width=600,format=auto,quality=85/assets/71339d65-a05d-4c02-a540-2260acdfc245/cherry-6.png",
  "https://assets.auxodev.dk/cdn-cgi/image/width=600,format=auto,quality=85/assets/71339d65-a05d-4c02-a540-2260acdfc245/cherry-7.png",
];

/**
 * CherryBranchImg: High-resolution botanical cherry branch illustration from design-elements
 */
export function CherryBranchImg({
  index = 1,
  className = "w-48 h-auto",
  flip = false,
  rotate = 0,
  opacity = "opacity-75",
  alt = "Kirsebærgren illustration",
}) {
  const safeIndex = (Math.abs(index) - 1) % CHERRY_ASSETS.length;
  const src = CHERRY_ASSETS[safeIndex] || CHERRY_ASSETS[0];

  const transformStyle = {
    transform: `${flip ? "scaleX(-1)" : ""} ${rotate ? `rotate(${rotate}deg)` : ""}`.trim(),
  };

  return (
    <img
      src={src}
      alt={alt}
      loading="lazy"
      aria-hidden="true"
      style={transformStyle.transform ? transformStyle : undefined}
      className={`pointer-events-none select-none object-contain transition-opacity duration-300 ${opacity} ${className}`}
    />
  );
}

/**
 * SakuraFlower: Delicate 5-petaled Cherry Blossom SVG with harmonious palette (#d74814, #ffe0cf)
 */
export function SakuraFlower({ className = "w-6 h-6", glow = false, petalColor = "#FFE0CF" }) {
  return (
    <svg
      viewBox="0 0 100 100"
      className={`inline-block ${className} ${glow ? "drop-shadow-[0_2px_8px_rgba(215,72,20,0.35)]" : ""}`}
      aria-hidden="true"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <defs>
        <radialGradient id="sakuraCenterGrad" cx="50%" cy="50%" r="50%">
          <stop offset="0%" stopColor="#D74814" />
          <stop offset="60%" stopColor="#F3A992" />
          <stop offset="100%" stopColor={petalColor} />
        </radialGradient>
        <linearGradient id="sakuraPetalGrad" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#FFFFFF" />
          <stop offset="50%" stopColor="#FFE0CF" />
          <stop offset="100%" stopColor="#F3A992" />
        </linearGradient>
      </defs>

      {/* 5 Petals */}
      {[0, 72, 144, 216, 288].map((angle, i) => (
        <g key={i} transform={`rotate(${angle} 50 50)`}>
          <path
            d="M50 50 C42 35 34 22 42 10 C46 5 48 6 50 11 C52 6 54 5 58 10 C66 22 58 35 50 50 Z"
            fill="url(#sakuraPetalGrad)"
            stroke="#F3A992"
            strokeWidth="0.8"
          />
          <path d="M50 48 L50 20" stroke="#D74814" strokeWidth="0.6" strokeOpacity="0.5" />
        </g>
      ))}

      {/* Center Pistil & Stamens */}
      <circle cx="50" cy="50" r="9" fill="url(#sakuraCenterGrad)" />
      
      {/* Golden/Warm pollen dots */}
      {[0, 45, 90, 135, 180, 225, 270, 315].map((ang, j) => {
        const rad = (ang * Math.PI) / 180;
        const x = 50 + Math.cos(rad) * 6;
        const y = 50 + Math.sin(rad) * 6;
        return (
          <g key={j}>
            <line x1="50" y1="50" x2={x} y2={y} stroke="#D74814" strokeWidth="0.8" strokeOpacity="0.8" />
            <circle cx={x} cy={y} r="1.2" fill="#D74814" />
          </g>
        );
      })}
      <circle cx="50" cy="50" r="3.5" fill="#593427" opacity="0.85" />
    </svg>
  );
}

/**
 * SakuraBranch: Renders cherry branch PNG from design-elements
 */
export function SakuraBranch({
  className = "w-48 sm:w-64 lg:w-80 h-auto",
  direction = "right",
  index = 1,
}) {
  const isLeft = direction === "left";
  return (
    <CherryBranchImg
      index={index}
      flip={isLeft}
      className={className}
      opacity="opacity-80"
    />
  );
}

/**
 * Clean Japanese-inspired badge without hieroglyphs
 */
export function JapaneseBadge({
  label = "JAPANSK AKUPUNKTUR",
  meaning = "Skånsom & præcis behandling",
  className = "",
}) {
  return (
    <div
      className={`inline-flex items-center space-x-2 py-2 px-3 rounded-xl bg-white/90 backdrop-blur-md border border-[#ffe0cf] shadow-xs ${className}`}
    >
      <SakuraFlower className="w-4 h-4 text-[#d74814] shrink-0" />
      <div className="flex flex-col">
        <span className="text-[10px] uppercase font-bold tracking-wider text-[#d74814]">
          {label}
        </span>
        {meaning && (
          <span className="text-[11px] text-[#593427] font-medium whitespace-nowrap">
            {meaning}
          </span>
        )}
      </div>
    </div>
  );
}

// Backward-compatibility aliases without hieroglyphs
export function HankoSeal({ className = "", size = "md" }) {
  const sizeClasses = {
    sm: "w-6 h-6",
    md: "w-8 h-8",
    lg: "w-10 h-10",
  };
  return (
    <div
      className={`inline-flex items-center justify-center rounded-full bg-[#ffe0cf]/60 border border-[#d74814]/30 text-[#d74814] shadow-xs ${
        sizeClasses[size] || sizeClasses.md
      } ${className}`}
      aria-hidden="true"
    >
      <SakuraFlower className="w-3.5 h-3.5" />
    </div>
  );
}

export function VerticalJapaneseBadge({
  label = "JAPANSK TRADITION",
  meaning = "Akupunktur & Meridianterapi",
  className = "",
}) {
  return (
    <JapaneseBadge label={label} meaning={meaning} className={className} />
  );
}

export function JapaneseVerticalRibbon({
  text = "KITA AKUPUNKTUR",
  subtext = "Risskov Aarhus",
  className = "",
}) {
  return null;
}

/**
 * JapaneseSectionHeader: Section heading with Sakura blossom & glass pill
 */
export function JapaneseSectionHeader({
  badge,
  title,
  subtitle,
  centered = true,
  className = "",
}) {
  return (
    <div className={`relative ${centered ? "text-center mx-auto" : ""} ${className}`}>
      <div className={`inline-flex items-center gap-2 mb-3.5 px-3.5 py-1.5 rounded-full bg-white/90 backdrop-blur-md border border-[#ffe0cf] shadow-xs ${centered ? "mx-auto" : ""}`}>
        <SakuraFlower className="w-4 h-4 text-[#d74814]" />
        <span className="text-xs font-semibold tracking-wider uppercase text-[#d74814]">
          {badge}
        </span>
      </div>

      <h2 className="text-2xl sm:text-3xl lg:text-4xl font-serif text-[#593427] leading-tight">
        {title}
      </h2>

      {subtitle && (
        <p className="mt-3 text-[#7a5b4f] max-w-2xl text-sm sm:text-base leading-relaxed mx-auto">
          {subtitle}
        </p>
      )}

      <div className={`flex items-center justify-center gap-2 mt-4 ${centered ? "mx-auto" : ""}`}>
        <div className="w-8 h-[1px] bg-[#d74814]/30" />
        <SakuraFlower className="w-3.5 h-3.5 opacity-80 text-[#d74814]" />
        <div className="w-8 h-[1px] bg-[#d74814]/30" />
      </div>
    </div>
  );
}
